/**
 * locale-unit.ts
 *
 * Infers the user's preferred measurement unit (cm vs inches) from the
 * browser's locale.  This is used as the default in self-assessment mode
 * where no authenticated user profile is available.
 *
 * Countries that conventionally use the imperial (inch-based) system:
 *   US  – United States
 *   LR  – Liberia
 *   MM  – Myanmar
 *
 * All other regions default to the metric system.
 */

export type DisplayUnit = 'cm' | 'inches';

/**
 * Returns the preferred height/measurement unit inferred from the browser's
 * locale.  Falls back to `'cm'` if the locale cannot be determined.
 */
export function detectLocaleUnit(): DisplayUnit {
  try {
    const locale =
      (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages && navigator.languages[0]))) ||
      '';

    if (!locale) return 'cm';

    const region = new Intl.Locale(locale).region?.toUpperCase() ?? '';
    const imperialRegions = new Set<string>(['US', 'LR', 'MM']);
    return imperialRegions.has(region) ? 'inches' : 'cm';
  } catch {
    // Intl.Locale not supported or invalid locale string — default to metric.
    return 'cm';
  }
}
